// ═══════════════════════════════════════════════════════════════════════════
// UIState — cookie-based persistence for user UI preferences
// ═══════════════════════════════════════════════════════════════════════════
// Cookies are readable by both JS and PHP, so PHP can render the right
// state server-side (no flash of unstyled content). Cookie names: biz_ui_<key>.
//
// Migrated from ca/js/ui-state.js into BSEE (2026-06-01) as part of the
// ca/js EOL. Exposed as window.UIState for inline scripts + cmdpal providers
// that reference it at parse time. Because the BSEE bundle loads in <head>,
// window.UIState is available earlier than the old body-bottom <script> — net
// safer for existing consumers.
//
// Event contract:
//   UIState.set(key, value) fires `bizui:changed` on window with
//   { detail: { key, value, prev } } when value actually changed.
//   Listeners filter by key prefix (e.g. 'cmdpal_*').
//   Cookies set by other means (PHP, devtools) do NOT fire the event.
//
// See _README/ui_cookies.readme.md for the full key registry.
// ═══════════════════════════════════════════════════════════════════════════

const PREFIX = 'biz_ui_';
const MAX_AGE = 365 * 24 * 60 * 60; // 1 year

export interface UIChangeDetail {
    key: string;
    value: string | null;
    prev: string | null;
}

function fireChanged(detail: UIChangeDetail): void {
    try {
        window.dispatchEvent(new CustomEvent('bizui:changed', { detail }));
    } catch {
        // Older engines without CustomEvent — ignore.
    }
}

function get(key: string): string | null {
    const name = PREFIX + key + '=';
    const parts = document.cookie.split(';');
    for (const part of parts) {
        const c = part.trim();
        if (c.indexOf(name) === 0) {
            const value = c.substring(name.length);
            // Treat an empty value as absent. Real browsers drop an expired
            // cookie outright (get → null), but some engines (and happy-dom)
            // keep `biz_ui_<key>=` after max-age=0. Normalizing here keeps
            // remove()/flushAll() deterministic and matches consumer
            // truthiness checks (`if (UIState.get(key))`).
            return value === '' ? null : value;
        }
    }
    return null;
}

function set(key: string, value: string | number | boolean): void {
    const prev = get(key);
    const next = String(value);
    document.cookie = PREFIX + key + '=' + next
        + ';path=/;max-age=' + MAX_AGE + ';SameSite=Lax';
    if (prev !== next) {
        fireChanged({ key, value: next, prev });
    }
}

function remove(key: string): void {
    const prev = get(key);
    document.cookie = PREFIX + key + '=;path=/;max-age=0';
    if (prev !== null) {
        fireChanged({ key, value: null, prev });
    }
}

function flushAll(): void {
    const parts = document.cookie.split(';');
    for (const part of parts) {
        const c = part.trim();
        if (c.indexOf(PREFIX) === 0) {
            const name = c.split('=')[0];
            document.cookie = name + '=;path=/;max-age=0';
        }
    }
    // Broadcast a single wildcard notice — listeners can re-read everything.
    fireChanged({ key: '*', value: null, prev: null });
}

export const uiState = { get, set, remove, flushAll };

// Expose globally for inline page scripts + cmdpal providers that reference
// UIState at parse time. No DOM dependency, so attach at module eval (the
// bundle <script> in <head> runs before any consumer).
(window as unknown as { UIState: typeof uiState }).UIState = uiState;
